/**
 * SOLID Review
 * S — Strong. One concern: LiveKit's signaling and admin reads on the server, as the `ClientMediaServer`
 *     port speaks them.
 * O — Good. A new media call is one method over the same adapter.
 * L — Strong. Implements the port interface exactly.
 * I — Good. The port's five ops — the whole media GraphQL surface.
 * D — Strong. The Application's adapter interface and the shared result mapping.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { PlayerId, RoomId } from '../../application/ports/ClientVocabulary';
import type {
  IPayloadMediaToken, IPayloadMediaConnection, IPayloadMediaPresence, IPayloadMediaHealth, IPayloadMediaUsage,
} from '../../domain/ports/schemas/MediaMoves';
import type { IPortClientMediaServer } from '../../application/ports/contracts';
import type { IAdapterApolloLiveKit } from '../../application/livekit/ports/IAdapterApolloLiveKit';
import { fromAdapter, doneFromAdapter } from '../graphql/wireResult';

/**
 * THE MEDIA GATEWAY (frontend strict-ports plan, FE5b, 2026-10-04), over `ApolloLiveKitAdapter` — the
 * Framework's `LiveKitAdapter` socket until now, called by useLiveKitSession, ControlPanel's admin line and
 * the usage report. The calls are unchanged (the token cached per room and player until it expires); an
 * adapter's failure becomes the port's `{ code, message }`, and the answers are the fields the screens read.
 */
export class ClientMediaServerAdapter implements IPortClientMediaServer {
  constructor(private readonly media: IAdapterApolloLiveKit) {}

  async token(_ctx: IContextRequest, request: IPayloadMediaToken) {
    return fromAdapter(await this.media.getLiveKitToken(request.roomId as RoomId, request.playerId as PlayerId), (t) => ({
      token: t.token, serverUrl: t.serverUrl,
    }));
  }

  async reportConnection(_ctx: IContextRequest, request: IPayloadMediaConnection) {
    return doneFromAdapter(await this.media.reportLiveKitConnectionState(request.roomId as RoomId, request.playerId as PlayerId, request.connected));
  }

  async markPresence(_ctx: IContextRequest, request: IPayloadMediaPresence) {
    return doneFromAdapter(await this.media.markMediaPresence(request.roomId as RoomId, request.usedVideo, request.usedAudio));
  }

  async health(_ctx: IContextRequest, request: IPayloadMediaHealth) {
    return fromAdapter(await this.media.getLiveKitHealth(request.playerId as PlayerId), (h) => ({
      configured: h.configured, activeRooms: h.activeRooms, activeParticipants: h.activeParticipants,
    }));
  }

  async usageReport(_ctx: IContextRequest, request: IPayloadMediaUsage) {
    return fromAdapter(await this.media.getLiveKitUsageReport(request.playerId as PlayerId, request.startDate, request.endDate), (r) => ({
      totalMinutes: r.totalMinutes,
      entries: r.entries.map((e) => ({ date: e.date, playerId: e.playerId, minutes: e.minutes })),
    }));
  }
}
